'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { type JSX, type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { type Dictionary, getDictionary } from '@src/i18n/dictionary';
import { getLocaleCodeFromCountryLang, getLocaleFromCode, getLocaleFromPath } from '@src/i18n/locale.util';
import type { CountryCodeLowercase, Lang, LocaleConfig } from '@src/i18n/locales';
interface LocaleContextType {
  dictionary: Dictionary;
  locale: LocaleConfig;
}
const LocaleContext = createContext<LocaleContextType | undefined>(undefined);
interface LocaleProviderProps {
  children: ReactNode;
  lang: Lang;
  country: CountryCodeLowercase;
}
export const LocaleProvider = props => {
  const $ = _c(19);
  const [country, setCountry] = useState(props.country);
  const [lang, setLang] = useState(props.lang);
  let t0;
  if ($[0] !== country || $[1] !== lang) {
    t0 = getDictionary(country, lang);
    $[0] = country;
    $[1] = lang;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const dictionary = t0;
  let t1;
  if ($[3] !== country || $[4] !== lang) {
    t1 = getLocaleCodeFromCountryLang(country, lang);
    $[3] = country;
    $[4] = lang;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const localeCode = t1;
  let t2;
  if ($[6] !== localeCode) {
    t2 = getLocaleFromCode(localeCode);
    $[6] = localeCode;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const locale = t2;
  const pathname = usePathname();
  let t3;
  let t4;
  if ($[8] !== country || $[9] !== lang || $[10] !== pathname) {
    t3 = () => {
      if (!pathname) {
        return;
      }
      const pathLocale = getLocaleFromPath(pathname);
      if (country !== pathLocale.countryCodeLowercase) {
        setCountry(pathLocale.countryCodeLowercase);
      }
      if (lang !== pathLocale.lang) {
        setLang(pathLocale.lang);
      }
    };
    t4 = [pathname, country, lang];
    $[8] = country;
    $[9] = lang;
    $[10] = pathname;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== dictionary || $[14] !== locale) {
    t5 = {
      dictionary,
      locale
    };
    $[13] = dictionary;
    $[14] = locale;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== props.children || $[17] !== t5) {
    t6 = <LocaleContext.Provider value={t5}>{props.children}</LocaleContext.Provider>;
    $[16] = props.children;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export const useLocale = (t0, customLocale) => {
  const $ = _c(10);
  const scope = t0 === undefined ? "common" : t0;
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleContext");
  }
  const locale = customLocale ?? context.locale;
  let t1;
  bb0: {
    if (customLocale) {
      let t2;
      if ($[0] !== customLocale.countryCodeLowercase || $[1] !== customLocale.lang) {
        t2 = getDictionary(customLocale.countryCodeLowercase, customLocale.lang);
        $[0] = customLocale.countryCodeLowercase;
        $[1] = customLocale.lang;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2;
      break bb0;
    }
    t1 = context.dictionary;
  }
  const dictionary = t1;
  const scopedDict = dictionary[scope];
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function interpolate(str, obj) {
      return (str ?? "").replace(/\{\{([^}]+)\}\}/g, (_, token) => String(obj[token ?? ""]));
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const interpolate = t2;
  let t3;
  if ($[4] !== scopedDict) {
    t3 = function translate(key, t4) {
      const tokens = t4 === undefined ? {} : t4;
      if (!key) {
        return "";
      }
      return interpolate(scopedDict[key as keyof typeof scopedDict], tokens);
    };
    $[4] = scopedDict;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const translate = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function transform(translatedString, components) {
      if (!components) {
        return <>{translatedString}</>;
      }
      return Object.entries(components).reduce(_temp, [translatedString]);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const transform = t4;
  let t5;
  if ($[7] !== locale || $[8] !== translate) {
    t5 = {
      t: translate,
      transform,
      locale
    };
    $[7] = locale;
    $[8] = translate;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export type TranslateFn = ReturnType<typeof useLocale>['t'];
function _temp(acc, t0) {
  const [key_0, valueFn] = t0;
  return acc.flatMap((el, index) => {
    if (typeof el === "string") {
      const [head, text, tail] = `${el}`.split(`<${key_0}>`);
      return [head, <span key={`${key_0}-${index}`}>{valueFn(text)}</span>, tail];
    }
    return el;
  });
}